import { BlockList } from '@zillow/cms-blocks';
import { Banner, isDarkBackgroundColor } from '@zillow/zrs-cms';
import React, { useCallback } from 'react';

import type { Block } from '../../types/blocks';
import type { ResolvedBanner } from '../../types/shared';
import {
    trackBannerClose,
    trackBannerImpression,
} from '../../util/bannerClickstream';

export type BannerTemplateProps = {
    /** Banner resolved server-side for this request; nothing renders without it. */
    banner?: ResolvedBanner | null;
    /** Site slug, for analytics context. */
    surface?: string;
    /** Post/page id the banner is rendering on, for analytics context. */
    contentId?: string;
    /** Disables persisted dismissal (previews, where hiding the subject is wrong). */
    persistDismissal?: boolean;
    /** Renders in place instead of portaling; for the document's server render. */
    disablePortal?: boolean;
};

const GROUP_BLOCK_NAME = 'constellation/group';

/**
 * Whether the banner's content sits on a dark background, so the close control
 * needs its light variant. The bar has no background of its own; authors set it
 * on a full-width Group, so this reads the root Group's background. The Group
 * editor stores it per viewport, so the `default` value is used. Anything else
 * (no root Group, no background) keeps the default dark icon.
 */
export const hasDarkBackground = (blocks: Block[]): boolean => {
    const root = blocks[0];
    if (root?.blockName !== GROUP_BLOCK_NAME) {
        return false;
    }
    const background = (
        root.attrs as
            | { backgroundColor?: string | { default?: string } }
            | undefined
    )?.backgroundColor;
    const key =
        typeof background === 'string' ? background : background?.default;
    return isDarkBackgroundColor(key);
};

/**
 * Renders the request's Banner post, the way FooterTemplate renders a Footer:
 * the post's blocks through BlockList, inside the bar. The background comes
 * from those blocks (a full-width Group); the close control is the Banner
 * post's meta.
 *
 * Unlike a footer it is not placed by the page templates. They render inside
 * `#__next`, below PageFrame's global top nav, and the banner has to sit above
 * it. `_document` server-renders it into a mount at the top of `<body>` and
 * `_app` renders it again on the client, where `Banner` portals into that
 * mount and takes over the server copy.
 */
const BannerTemplate: React.FC<BannerTemplateProps> = ({
    banner,
    surface,
    contentId,
    persistDismissal = true,
    disablePortal = false,
}) => {
    const dismissible = Boolean(banner?.meta?.dismissible);

    const context = {
        bannerId: banner?.id || '',
        dismissible,
        surface,
        contentId,
    };

    const onImpression = useCallback(
        () => trackBannerImpression(context),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [context.bannerId, context.dismissible, surface, contentId]
    );
    const onClose = useCallback(
        () => trackBannerClose(context),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [context.bannerId, context.dismissible, surface, contentId]
    );

    if (!banner?.id || !banner.blocks?.length) {
        return null;
    }

    return (
        <Banner
            bannerId={banner.id}
            darkBackground={hasDarkBackground(banner.blocks)}
            dismissible={dismissible}
            persistDismissal={persistDismissal}
            disablePortal={disablePortal}
            onImpression={onImpression}
            onClose={onClose}
        >
            <BlockList blocks={banner.blocks} />
        </Banner>
    );
};

export default BannerTemplate;
